// src/hooks/useTicketFields.js
// Custom hook that fetches and caches ticket field definitions from the Zoho portal API.
// Uses a module-level cache so the API is called only once per page load.

import { useState, useEffect } from 'react';
import { fetchTicketFields, parseTicketFields } from '../services/ticketFields';

// Module-level cache — survives re-renders and step navigation
let _cachedFields = null;
let _fetchPromise = null;

export function useTicketFields() {
  const [fields, setFields] = useState(_cachedFields);
  const [isLoading, setIsLoading] = useState(_cachedFields === null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (_cachedFields) {
      return;
    }

    // Deduplicate concurrent calls — share the same in-flight promise
    if (!_fetchPromise) {
      _fetchPromise = fetchTicketFields()
        .then((data) => {
          _cachedFields = parseTicketFields(data);
          return _cachedFields;
        })
        .catch((err) => {
          _fetchPromise = null; // allow retry on next render
          throw err;
        });
    }

    _fetchPromise
      .then((parsed) => {
        setFields(parsed);
        setError(null);
      })
      .catch((err) => {
        setError(err.message ?? 'Failed to load form options');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  return { fields, isLoading, error };
}
